import React, { useState, useEffect, useRef } from 'react';

// Single-line text that, when too long for its box, pauses, then scrolls left
// in one direction with a copy following behind, so it loops seamlessly.
// Short text just sits still.
const SCROLL_SPEED = 25; // px per second, constant like a music player
const PAUSE_MS = 1500;   // pause at the start of each loop
const GAP = 24;          // px between the end of the text and its copy

export default function ScrollingText({ text, className, style, maxWidth }) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const textRef = useRef(null);
  const [textWidth, setTextWidth] = useState(0);
  // Compare against the max width directly rather than the box's laid-out width:
  // some browsers (Safari) ignore max-width on nested flex items and let the
  // box grow to the full text, which would mean it never scrolls.
  const maxPx = parseFloat(maxWidth) || Infinity;
  const overflowing = textWidth - maxPx > 1;

  // Measure the text's natural width (and again if a web font loads late)
  useEffect(() => {
    const measure = () => {
      if (textRef.current) setTextWidth(textRef.current.offsetWidth);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(textRef.current);
    return () => observer.disconnect();
  }, [text]);

  const reducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const looping = overflowing && !reducedMotion;

  // Pause, then scroll exactly one text-plus-gap width. At that point the copy
  // sits where the original started, so restarting the loop is invisible.
  useEffect(() => {
    const el = innerRef.current;
    if (!looping || !el || !el.animate) return;
    const distance = textWidth + GAP;
    const scrollMs = (distance / SCROLL_SPEED) * 1000;
    const total = PAUSE_MS + scrollMs;
    const animation = el.animate(
      [
        { transform: 'translateX(0)', offset: 0 },
        { transform: 'translateX(0)', offset: PAUSE_MS / total, easing: 'linear' },
        { transform: `translateX(-${distance}px)`, offset: 1 },
      ],
      { duration: total, iterations: Infinity }
    );
    return () => animation.cancel();
  }, [looping, textWidth, text]);

  return (
    <div
      ref={outerRef}
      className={className}
      style={{
        ...style,
        maxWidth,
        // Pin the width explicitly when too long, so the box can't stretch
        ...(overflowing && { width: maxPx, flexShrink: 0 }),
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: reducedMotion ? 'ellipsis' : 'clip',
        // Right-aligned parents would push overflowing text off the left edge
        ...(looping && { textAlign: 'left' }),
      }}
    >
      <span ref={innerRef} style={{ display: 'inline-block', willChange: looping ? 'transform' : undefined }}>
        <span ref={textRef} style={{ display: 'inline-block' }}>{text}</span>
        {looping && (
          <span aria-hidden="true" style={{ display: 'inline-block', paddingLeft: GAP }}>{text}</span>
        )}
      </span>
    </div>
  );
}
